import S, { DataSignal } from 's-js';
import { lift } from 's-array';
import { memoize } from 'lib/memoize';
import { notNullish } from 'lib/notNullish';
import uniqueIndex from 'lib/uniqueIndex';

import { AddCoordinationCardsRequest, AddCoordinationCardRequest } from 'data/models/AddCoordinationCardsRequest';
import { Coordination } from 'data/models/Coordination';
import { CoordinationCard } from 'data/models/CoordinationCard';
import { CoordinationCurrency } from 'server/enums/CoordinationCurrency';
import { CoordinationProvider } from 'data/models/CoordinationProvider';
import { PortCall } from 'data/models/PortCall';
import { ScheduleDateEntity as DateEntity } from 'data/models/ScheduleDateEntity';

import {
    MetroMapFramework,
    MetroMapFrameworkParams,
    MetroMapMilestone,
    MetroMapMilestoneParams,
    MilestoneParams
} from 'portcall/metromapframework/model';
import { CoordinationProviderCurrency } from 'server/enums/CoordinationProviderCurrency';

export interface SelectCoordinationsParams {
    PortCall : PortCall;
    SelectionStorage : CoordinationSelectionStorageFactory;
}

/**
 * A factory that when given a `DateEntity` + `Coordination` pair can provide an appropriate
 * `CoordinationSelectionStorage` implementation.
 */
export interface CoordinationSelectionStorageFactory {
    (date : DateEntity) : (coordination : Coordination) => CoordinationSelectionStorage;
}

/**
 * An object that can read/write `CoordinationSelectionData` for a particular `DateEntity` + `Coordination`
 * pair.
 */
export interface CoordinationSelectionStorage {
    Selection : DataSignal<CoordinationSelectionData | null>;
    /**
     * Does the proposed `DateEntity` + `Coordination` pair duplicate an existing C-Card?
     */
     DuplicatesExistingCCard : () => boolean;
}

export interface CoordinationSelectionData {
    readonly ProviderId : number;
}

function CoordinationSelectionDataEq(d1 : CoordinationSelectionData, d2 : CoordinationSelectionData) {
    return d1.ProviderId === d2.ProviderId;
}

export interface AddCCardsRequestCoordinationSelectionStorageParams {
    Request : AddCoordinationCardsRequest;
}

/**
 * A `CoordinationSelectionStorageFactory` implementation backed by the provided `AddCoordinationCardsRequest`
 * instance. This implementation uses a keying strategy that assumes that all milestone dates encountered have
 * unique Ids, so it's best suited for working with milestone dates that have already been persisted.
 */
 export function AddCCardsRequestCoordinationSelectionStorage({ Request } : AddCCardsRequestCoordinationSelectionStorageParams) : CoordinationSelectionStorageFactory {
    const ccardReqIndex = uniqueIndex(Request.RequestedCards, AddCoordinationCardRequestKey);
    return (date : DateEntity) => {
        const foundCoordinations = S(() => {
            const found = new Set<Coordination>();
            for (let c of date.CoordinationCards()) {
                if (c.Coordination()) found.add(c.Coordination()!);
            }
            return found;
        });
        return (coordination : Coordination) : CoordinationSelectionStorage => {
            const
                ccardReq = ccardReqIndex(() => AvailableCoordinationKey(date, coordination)),
                selection = S<CoordinationSelectionData | null>(() => ccardReq() && {
                    ProviderId: ccardReq()!.ProviderId()
                });
            return {
                Selection: (sel? : CoordinationSelectionData | null) =>
                    sel === undefined ? selection() :
                    S.sample(() => {
                        if (sel === null) {
                            if (ccardReq()) Request.RequestedCards.remove(ccardReq()!);
                        } else {
                            if (ccardReq()) {
                                ccardReq()!.ProviderId(sel.ProviderId);
                            } else {
                                Request.RequestedCards.push(new AddCoordinationCardRequest({
                                    ScheduleDateId: date.Id(),
                                    CoordinationId: coordination.Id(),
                                    ProviderId: sel.ProviderId
                                }));
                            }
                        }
                        return sel;
                    }),
                DuplicatesExistingCCard: () => foundCoordinations().has(coordination)
            };
        };
    };
}

// NOTE: Make sure AddCoordinationCardRequestKey and AvailableCoordinationKey produce matching key formats.
function AddCoordinationCardRequestKey(ccardReq : AddCoordinationCardRequest) {
    return `${ccardReq.ScheduleDateId()}:${ccardReq.CoordinationId()}`;
}

function AvailableCoordinationKey(Date : DateEntity, Coordination : Coordination) {
    return `${Date.Id()}:${Coordination.Id()}`;
}

export interface ScheduleDateCoordinationSelectionStorageParams { } // Nothing for now.

/**
 * A `CoordinationSelectionStorageFactory` implementation that stores selected coordinations inside the
 * `CoordinationCards` collection of encountered milestone dates. This implementation assumes it will only
 * encounter milestone dates that haven't been persisted yet.
 */
export function ScheduleDateCoordinationSelectionStorage({ } : ScheduleDateCoordinationSelectionStorageParams) : CoordinationSelectionStorageFactory {
    return (date : DateEntity) => (coordination : Coordination) : CoordinationSelectionStorage => {
        const
            ccard = S(() => date.CoordinationCards().find(ccard => ccard.CoordinationId() === coordination.Id()) ?? null),
            selection = S<CoordinationSelectionData | null>(() => ccard() && {
                ProviderId: ccard()!.ProviderId()
            });
        return {
            Selection: (sel? : CoordinationSelectionData | null) =>
                sel === undefined ? selection() :
                S.sample(() => {
                    if (sel === null) {
                        if (ccard()) date.CoordinationCards.remove(ccard()!);
                    } else {
                        if (ccard()) {
                            ccard()!.ProviderId(sel.ProviderId);
                        } else {
                            date.CoordinationCards.push(new CoordinationCard({
                                Id: 0,
                                CoordinationId: coordination.Id(),
                                ProviderId: sel.ProviderId
                            }));
                        }
                    }
                    return sel;
                }),
            // B/c we're assuming we're dealing w/ non-persisted date entities, we can also assume that there
            // are no existing C-Cards for this milestone date.
            DuplicatesExistingCCard: () => false
        };
    };
}

export type SelectCoordinations = ReturnType<typeof SelectCoordinations>;
export function SelectCoordinations(
    metroMapFrameworkModel : <TMilestone>(params : MetroMapFrameworkParams<TMilestone>) => MetroMapFramework<TMilestone>,
    metroMapMilestoneModel : (params : MetroMapMilestoneParams) => MetroMapMilestone,
    {
        PortCall,
        SelectionStorage
    } : SelectCoordinationsParams
) {
    const
        // Mini DI kernel...
        providerModel = (p : CoordinationProvider) => CoordinationProviderViewModel(p),
        coordinationModel = memoize((c : Coordination) => CoordinationViewModel(providerModel, c)),
        availableCoordinationModel = AvailableCoordination,
        milestoneModel = (params : MilestoneParams) => SelectCoordinationsMilestone(
            metroMapMilestoneModel,
            SelectionStorage,
            coordinationModel,
            availableCoordinationModel,
            params
        ),
        Framework = metroMapFrameworkModel({
            PortCall,
            MilestoneConstructor: milestoneModel
        }),
        IsValidForSubmit = Framework.Milestones.every(m => m.IsValidForSubmit()),
        HasActionableContent = Framework.Milestones.some(m => m.HasActionableContent());

    return {
        Framework,
        IsValidForSubmit,
        HasActionableContent
    };
}

export type CoordinationViewModel = ReturnType<typeof CoordinationViewModel>;
function CoordinationViewModel(
    providerModel : (p : CoordinationProvider) => CoordinationProviderViewModel,
    Coordination : Coordination
) {
    const AvailableProviders = Coordination
        .Providers
        .filter(p => p.Currency() === CoordinationProviderCurrency.Current)
        .map(providerModel)
        .orderBy(p => p.Provider.DisplayOrder());

    return { Coordination, AvailableProviders };
}

export type CoordinationProviderViewModel = ReturnType<typeof CoordinationProviderViewModel>;
function CoordinationProviderViewModel(
    Provider : CoordinationProvider
) {
    return { Provider };
}

export type SelectCoordinationsMilestone = ReturnType<typeof SelectCoordinationsMilestone>;
function SelectCoordinationsMilestone(
    metroMapMilestoneModel : (params : MetroMapMilestoneParams) => MetroMapMilestone,
    SelectionStorage : CoordinationSelectionStorageFactory,
    coordinationModel : (c : Coordination) => CoordinationViewModel,
    availableCoordinationModel : (params : AvailableCoordinationParams) => AvailableCoordination,
    params : MilestoneParams
) {
    const
        { Date } = params,
        metroMapMilestone = metroMapMilestoneModel(params),
        storage = SelectionStorage(Date),
        AvailableCoordinations =
            lift(() =>
                Date.MilestoneType()?.Coordinations().map(c => c.Coordination()).filter(notNullish) ?? []
            )
            .filter(c =>
                c.Currency() === CoordinationCurrency.Current &&
                c.AppliesToScheduleDate(Date)
            )
            .map(c => ({ c, storage: storage(c) }))
            .filter(({ storage }) => !storage.DuplicatesExistingCCard())
            .map(({ c, storage }) => availableCoordinationModel({
                Coordination: coordinationModel(c),
                Storage: storage
            }))
            .orderBy(c => c.Coordination.Coordination.DisplayOrder()),
        IsValidForSubmit = AvailableCoordinations.every(c => c.IsValidForSubmit()),
        HasActionableContent = AvailableCoordinations.some(c => c.HasActionableContent());

    return {
        ...metroMapMilestone,
        AvailableCoordinations,
        IsValidForSubmit,
        HasActionableContent
    };
}

/** Coordination is not selected. */
export interface AvailableCoordinationStateNotSelected {
    selected : false;
}

/** Coordination is provisionally selected but may or may not have had all necessary selection data entered. */
export interface AvailableCoordinationStateSelected {
    selected : true;
    data? : CoordinationSelectionData;
}

export type AvailableCoordinationState = AvailableCoordinationStateNotSelected | AvailableCoordinationStateSelected;

function AvailableCoordinationStateEq(state1 : AvailableCoordinationState, state2 : AvailableCoordinationState) {
    return (
        !state1.selected && !state2.selected ||
         state1.selected &&  state2.selected &&  !state1.data &&  !state2.data ||
         state1.selected &&  state2.selected && !!state1.data && !!state2.data && CoordinationSelectionDataEq(state1.data, state2.data)
    );
}

/**
 * Is this `AvailableCoordinationState` valid for submitting to the server. Rules out the state where the given
 * coordination has been provisionally selected but the user hasn't provided all necessary selection data yet.
 */
function IsStateValidForSubmit(state : AvailableCoordinationState) {
    return !state.selected || !!state.data;
}

interface AvailableCoordinationParams {
    Coordination : CoordinationViewModel;
    Storage : CoordinationSelectionStorage;
}

export type AvailableCoordination = ReturnType<typeof AvailableCoordination>;
function AvailableCoordination({
    Coordination,
    Storage
} : AvailableCoordinationParams) {
    const
        State = S.value({ selected: false }, AvailableCoordinationStateEq),
        IsValidForSubmit = () => IsStateValidForSubmit(State()),
        HasActionableContent = () =>
            // Non-mandatory coordinations are actionable b/c users can toggle them on/off
            !Coordination.Coordination.IsMandatory() ||
            // Coordinations w/ at least 2 providers are actionable b/c users can choose the provider
            Coordination.AvailableProviders().length > 1

    S(() => {
        // Having stored selection data ALWAYS means that the current state is selected. Note that the converse
        // does NOT hold; we can be selected w/o having had all necessary selection data entered.
        const sel = Storage.Selection();
        if (sel) State({ selected: true, data: sel });
    });

    S(() => {
        // Make sure Mandatory coordinations are auto-selected.
        if (Coordination.Coordination.IsMandatory() && !State().selected) State({ selected: true });
    });

    S(() => {
        // Auto-select the sole provider if the current state is selected and there's only one provider.
        if (State().selected && Coordination.AvailableProviders().length === 1) {
            Request(Coordination.AvailableProviders()[0].Provider.ProviderId());
        }
    });

    return {
        Coordination,
        State: () => State(),
        IsValidForSubmit,
        HasActionableContent,
        Request,
        CancelRequest
    };

    /**
     * Request this coordination for this milestone date, optionally specifying the desired providerId.
     */
    function Request(providerId? : number) {
        if (providerId === undefined) {
            // Set State directly.
            State({ selected: true })
        } else {
            // Set State indirectly by notifying our backing storage of the selection.
            Storage.Selection({ ProviderId: providerId });
        }
    }

    /** Cancel the pending request for this coordination for this milestone date, if any. */
    function CancelRequest() {
        S.freeze(() => (State({ selected: false }), Storage.Selection(null)));
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/selectcoordinations/model.ts